"use client";

import { useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "./ui/icons.js";
import type { User } from "@/types/index.js";
import { Button } from "./ui/button.js";
import { Link } from "react-router-dom";
import {
  Rss,
  Route,
  ChartNoAxesCombined,
  Settings,
  LucideProps,
} from "lucide-react";
import { ForwardRefExoticComponent, RefAttributes } from "react";
import Username from "./Username.js";

type NavItem = {
  name: string;
  href: string;
  Icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
};

interface NavbarProps {
  user: User | null;
  onLogin: (initialTab?: "signin" | "signup") => void;
  onLogout: () => void;
}

const navigation: NavItem[] = [
  // { name: "Dashboard", href: "/" },
  {
    name: "Startup Social",
    href: "/startup-social",
  },
  { name: "Startup News", href: "/startup-news" },
  { name: "Contact Us", href: "/ContactUs" },
];

export default function Navbar({ user, onLogin, onLogout }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems: NavItem[] = user
    ? [
        {
          Icon: Route,
          name: "Path to Investors",
          href: "/startup-spark-suite/investors",
        },
        { Icon: Rss, name: "Startup News", href: "/startup-news" },
        {
          Icon: ChartNoAxesCombined,
          name: "Startup Analytics",
          href: "/startup-analytics",
        },
        { Icon: Settings, name: "Campaign Settings", href: "/settings" },
      ]
    : navigation;

  return (
    <header className="border-b border-slate-300 bg-gradient-to-br from-[#B7B7B7] via-[#F7F7F7] to-[#F9F9F9] backdrop-blur-sm sticky top-0 z-50">
      <nav
        aria-label="Global"
        className="container mx-auto px-4 py-4 flex justify-between items-center"
      >
        <Link to="/" className="-m-1.5 p-1.5">
          <span className="sr-only">Startup Marquee</span>
          <span className="text-xl font-bold text-gray-900 font-roboto-condensed flex justify-center">
            <img
              src="/images/starupIconRed.png"
              alt="Logo"
              className="inline-block h-8 w-8 mr-2"
            />
            Startup Marquee
          </span>
        </Link>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-12">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="text-sm/6 font-semibold text-gray-900 flex items-center gap-x-2"
            >
              {item.Icon && <item.Icon className="size-4" />}
              {item.name}
            </Link>
          ))}
        </div>
        <div className="hidden lg:flex lg:items-center lg:gap-x-4">
          {user ? (
            <>
              <span className="text-slate-700">Welcome, {user.name}!</span>
              <Button
                variant="outline"
                onClick={onLogout}
                className="text-slate-700 border-slate-600 hover:bg-slate-800 hover:text-white"
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onLogin("signin")}
                className="text-black border-slate-400 hover:bg-slate-200 hover:text-slate-700"
              >
                Sign In
              </Button>
              <Button
                onClick={() => onLogin("signup")}
                className="bg-slate-700 hover:bg-slate-800 text-white"
              >
                Sign Up
              </Button>
            </>
          )}
        </div>
      </nav>
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">Startup Marquee</span>
              <span className="text-xl font-bold text-gray-900 font-roboto-condensed">
                Startup
              </span>
              <span className="text-xl font-bold text-gray-700">Marquee</span>
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.Icon && (
                      <item.Icon className="inline-block size-4 mr-2" />
                    )}
                    {item.name}
                  </Link>
                ))}
              </div>
              <div className="py-6">
                {user ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Log out
                  </a>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onLogin("signin");
                      setMobileMenuOpen(false);
                    }}
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                  >
                    Log in
                  </a>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
